import { useState, useEffect } from 'react';
import ChatList from './ChatList';
import ChatMessages from './ChatMessages';
import { Button } from '../../../components/ui/button';
import ChatContactInfo from './ChatContactInfo';
import WahaHistoryPanel from './WahaHistoryPanel';
import { leadMessagesApi, LeadMessage } from '../services/leadMessagesApi';
import { LeadWithAgent } from '../services/leadsApi';
import { Loader2, History } from 'lucide-react';

// Formatear fecha/hora en zona horaria de Bogotá, Colombia
const formatBogotaDateTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleString('es-CO', {
        timeZone: 'America/Bogota',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

interface DisplayMessage {
    id: string;
    text: string;
    isAgent: boolean;
    timestamp: string;
    avatar?: string;
    mediaUrl?: string | null;
}

interface InboxListViewProps {
    selectedFlowId?: string;
    selectedAgentId?: string;
    initialLeadId?: string;
}

export default function InboxListView({ selectedFlowId, selectedAgentId, initialLeadId }: InboxListViewProps) {
    const [selectedLead, setSelectedLead] = useState<LeadWithAgent | null>(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showWahaHistory, setShowWahaHistory] = useState(false);
    const [messages, setMessages] = useState<DisplayMessage[]>([]);
    const [loading, setLoading] = useState(false);

    // Load messages when a lead is selected
    useEffect(() => {
        const fetchMessages = async () => {
            console.log('[InboxListView] 🔄 Lead seleccionado:', selectedLead);
            console.log('[InboxListView] 📱 telefono_lead:', selectedLead?.telefono_lead);
            console.log('[InboxListView] 📞 numero_telefono:', selectedLead?.numero_telefono);

            if (!selectedLead?.telefono_lead) {
                console.log('[InboxListView] ⚠️ No hay telefono_lead, limpiando mensajes');
                setMessages([]);
                return;
            }

            try {
                setLoading(true);
                console.log('[InboxListView] 🔍 Cargando mensajes para:', selectedLead.telefono_lead);

                const leadMessages = await leadMessagesApi.getMessagesByPhone(
                    selectedLead.telefono_lead,
                    selectedLead.numero_telefono
                );

                console.log('[InboxListView] 📨 Mensajes recibidos:', leadMessages.length, leadMessages);

                // Convert LeadMessage to DisplayMessage format
                // Filtrar mensajes vacíos (sin contenido de texto y sin media)
                const displayMessages: DisplayMessage[] = leadMessages
                    .filter((msg: LeadMessage) => {
                        const hasText = msg.content && msg.content.trim() !== '';
                        const hasMedia = msg.media_url && msg.media_url.trim() !== '';
                        return hasText || hasMedia;
                    })
                    .map((msg: LeadMessage) => ({
                        id: msg.id,
                        text: msg.content,
                        isAgent: msg.sender_type === 'ai',
                        timestamp: formatBogotaDateTime(msg.created_at),
                        avatar: undefined,
                        mediaUrl: msg.media_url || null
                    }));

                console.log('[InboxListView] ✅ Mensajes formateados:', displayMessages);
                setMessages(displayMessages);
            } catch (error) {
                console.error('[InboxListView] ❌ Error fetching messages:', error);
                setMessages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [selectedLead?.telefono_lead, selectedLead?.numero_telefono]);

    const handleSendMessage = (messageText: string) => {
        const newMessage: DisplayMessage = {
            id: Date.now().toString(),
            text: messageText,
            isAgent: true,
            timestamp: formatBogotaDateTime(new Date().toISOString()),
            avatar: undefined
        };
        setMessages(prev => [...prev, newMessage]);
    };

    const handleLeadSelect = (lead: LeadWithAgent) => {
        console.log('[InboxListView] 👆 Lead seleccionado desde ChatList:', lead);
        setSelectedLead(lead);
    };

    return (
        <div className="flex h-full bg-background overflow-hidden">
            <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 flex min-h-0">
                    <ChatList
                        selectedChatId={selectedLead?.id}
                        onChatSelect={handleLeadSelect}
                        flowId={selectedFlowId}
                        agentId={selectedAgentId}
                        initialLeadId={initialLeadId}
                    />
                    <div className="min-w-[420px] flex-1 flex flex-col min-h-0">
                        <div className="flex-1 flex flex-row gap-6 px-6 h-full min-h-0">
                            <div className="flex-1 flex flex-col min-h-0">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-3 border-b bg-white rounded-t-2xl">
                                    <span className="font-semibold text-lg text-maxi-text-dark">
                                        {selectedLead?.name || selectedLead?.telefono_lead || 'Contacto'}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        {selectedAgentId && (
                                            <Button
                                                type="button"
                                                onClick={() => setShowWahaHistory(true)}
                                                variant="outline"
                                                className="px-3 py-2 rounded-full font-medium text-sm gap-1.5"
                                            >
                                                <History className="w-4 h-4" />
                                                Historial WhatsApp
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            onClick={() => setShowContactModal(true)}
                                            variant="default"
                                            className="px-4 py-2 rounded-full font-medium shadow hover:bg-white hover:text-maxi-green transition"
                                        >
                                            Detalle Contacto
                                        </Button>
                                    </div>
                                </div>
                                {loading ? (
                                    <div className="flex-1 flex items-center justify-center bg-white">
                                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <ChatMessages
                                        messages={messages}
                                        onSendMessage={handleSendMessage}
                                        contactName={selectedLead?.name || selectedLead?.telefono_lead || 'Contacto'}
                                    />
                                )}
                                <ChatContactInfo
                                    contact={{
                                        id: selectedLead?.id,
                                        name: selectedLead?.name || 'Contacto',
                                        company: selectedLead?.company || '',
                                        email: selectedLead?.email || '',
                                        phone: selectedLead?.telefono_lead || '',
                                        value: selectedLead?.value ? `$${selectedLead.value}` : '$0',
                                        stage: selectedLead?.tags || 'nuevo',
                                        ia_activa: selectedLead?.ia_activa,
                                    }}
                                    isOpen={showContactModal}
                                    onClose={() => setShowContactModal(false)}
                                />
                                <WahaHistoryPanel
                                    isOpen={showWahaHistory}
                                    onClose={() => setShowWahaHistory(false)}
                                    agentId={selectedAgentId || ''}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
